import React, { forwardRef } from "react";
import { formatMoney, SIZE_LABELS, REPAIR_LABELS } from "@/lib/pricing";
import { fmtDate } from "@/lib/quoteText";
import { BRAND } from "@/lib/brand";

const NAVY = "#1e3a8a";
// A4 at 96dpi: 210mm × 297mm
export const A4_WIDTH_PX = 793;
export const A4_HEIGHT_PX = 1100;

function Header({ doc }) {
  const { shop } = doc;
  return (
    <div className="flex items-start justify-between gap-6 pb-4" style={{ borderBottom: `3px solid ${NAVY}` }}>
      <img src={BRAND.logo} alt={shop.name} className="w-[150px] h-[150px] object-contain -my-3 -ml-2" crossOrigin="anonymous" />
      <div className="text-right">
        <div className="text-[22px] font-black leading-tight" style={{ color: NAVY }}>{shop.name}</div>
        <div className="text-[11px] tracking-[0.2em] uppercase text-slate-500 mt-0.5">{shop.tagline}</div>
        {shop.address && <div className="text-[12px] text-slate-700 mt-3 whitespace-pre-line">{shop.address}</div>}
        <div className="text-[12px] text-slate-700">{[shop.phone, shop.email].filter(Boolean).join("  ·  ")}</div>
      </div>
    </div>
  );
}

function Meta({ doc }) {
  const size = SIZE_LABELS[doc.size];
  const Cell = ({ l, v }) => v ? <div><span className="text-[10px] uppercase tracking-wider text-slate-500">{l}</span><div className="text-[13px] font-semibold text-slate-900">{v}</div></div> : null;
  return (
    <div className="grid grid-cols-2 gap-6 py-4">
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Bill To · 客户</div>
        <div className="text-[15px] font-bold text-slate-900">{doc.customer.name || "Walk-in customer · 临时客户"}</div>
        {doc.customer.uen && <div className="text-[11px] text-slate-600">UEN {doc.customer.uen}</div>}
        {doc.customer.address && <div className="text-[11px] text-slate-600">{doc.customer.address}</div>}
        {doc.customer.contact && <div className="text-[11px] text-slate-600">Attn: {doc.customer.contact}</div>}
        {doc.customer.phone && <div className="text-[12px] text-slate-700 font-mono">{doc.customer.phone}</div>}
        <div className="grid grid-cols-2 gap-2 pt-1">
          <Cell l="Plate 车牌" v={doc.vehicle.plate} />
          <Cell l="Vehicle 车辆" v={doc.vehicle.model} />
          <Cell l="Paint Code 色号" v={doc.vehicle.paintCode} />
          <Cell l="Size 车型" v={size ? `${size.en} ${size.zh}` : ""} />
        </div>
      </div>
      <div className="text-right">
        <div className="text-[28px] font-black tracking-tight" style={{ color: NAVY }}>{doc.kind === "invoice" ? "INVOICE" : "QUOTATION"}</div>
        <div className="text-[13px] text-slate-500 -mt-1">{doc.kind === "invoice" ? "发票" : "报价单"}</div>
        <div className="mt-3 text-[12px] text-slate-700 space-y-0.5">
          <div><span className="text-slate-500">{doc.kind === "invoice" ? "Invoice no." : "Ref"}</span> <span className="font-mono font-semibold">{doc.ref}</span></div>
          {doc.quoteRef && <div><span className="text-slate-500">Quote ref</span> <span className="font-mono">{doc.quoteRef}</span></div>}
          <div><span className="text-slate-500">Date</span> <span className="font-semibold">{fmtDate(doc.created_at)}</span></div>
          {doc.dueDate && <div><span className="text-slate-500">Due</span> <span className="font-semibold" style={{ color: "#b45309" }}>{fmtDate(doc.dueDate)}</span> <span className="text-slate-500">({doc.termsDays}-day terms)</span></div>}
          {doc.kind === "invoice" && doc.status === "paid" && <div className="inline-block mt-1 px-2 py-0.5 rounded text-[11px] font-bold" style={{ background: "#dcfce7", color: "#166534" }}>PAID 已付款</div>}
          {doc.shop.gstRegNo && <div><span className="text-slate-500">GST Reg.</span> <span className="font-mono">{doc.shop.gstRegNo}</span></div>}
        </div>
      </div>
    </div>
  );
}

function Items({ doc, sym }) {
  const th = "text-[10px] uppercase tracking-wider text-white py-2 px-3 text-left";
  const td = "py-2 px-3 text-[12px] text-slate-800 align-top border-b border-slate-200";
  return (
    <table className="w-full border-collapse">
      <thead>
        <tr style={{ background: NAVY }}>
          <th className={th}>Item · 项目</th>
          <th className={`${th} text-right w-[90px]`}>Respray</th>
          <th className={`${th} text-right w-[110px]`}>Panel Beating · 钣金补土</th>
          <th className={`${th} text-right w-[90px]`}>Tricoat Upgrade</th>
          <th className={`${th} text-right w-[100px]`}>Amount</th>
        </tr>
      </thead>
      <tbody>
        {doc.panels.map((it) => it.kind === "imported" || (!it.en && it.description) ? (
          <tr key={it.key || it.description}>
            <td className={td} colSpan={4}>
              <div className="font-semibold" data-testid="invoice-imported-item">{it.description}{it.qty > 1 ? ` ×${it.qty}` : ""}</div>
              {it.detail && <div className="text-[11px] text-slate-500">{it.detail}</div>}
            </td>
            <td className={`${td} text-right font-mono font-bold`}>{formatMoney(sym, it.total)}</td>
          </tr>
        ) : (
          <tr key={it.key}>
            <td className={td}>
              <div className="font-semibold">{it.en}{it.qty > 1 ? ` ×${it.qty}` : ""}{it.sprayMode === "touchup" && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#e0f2fe", color: "#0369a1" }}>Touch-up</span>}</div>
              {it.zh && <div className="text-[11px] text-slate-500">{it.zh}</div>}
            </td>
            <td className={`${td} text-right font-mono`}>{formatMoney(sym, it.respray)}{it.sprayMode === "touchup" && <div className="text-[10px] text-slate-500 font-sans">Touch-up</div>}</td>
            <td className={`${td} text-right font-mono`}>{it.repair > 0 ? <>{formatMoney(sym, it.repair)}<div className="text-[10px] text-slate-500 font-sans">{REPAIR_LABELS[it.repairTier]?.en}</div></> : "—"}</td>
            <td className={`${td} text-right font-mono`}>{it.special > 0 ? formatMoney(sym, it.special) : "—"}</td>
            <td className={`${td} text-right font-mono font-bold`}>{formatMoney(sym, it.total)}</td>
          </tr>
        ))}
        {doc.services.map((s) => (
          <tr key={s.id}>
            <td className={td} colSpan={4}>
              <div className="font-semibold">{s.en}</div>
              <div className="text-[11px] text-slate-500">{s.zh}</div>
            </td>
            <td className={`${td} text-right font-mono font-bold`}>{formatMoney(sym, s.price)}</td>
          </tr>
        ))}
        {(doc.parts || []).map((p) => (
          <tr key={p.id}>
            <td className={td} colSpan={4}>
              <div className="font-semibold">{p.name} <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#e0f2fe", color: "#0369a1" }}>Spare part · net</span></div>
              <div className="text-[11px] text-slate-500">Part {formatMoney(sym, p.price)}{p.ri > 0 ? ` + R&I labour ${formatMoney(sym, p.ri)}` : ""} · 零件 (不打折)</div>
            </td>
            <td className={`${td} text-right font-mono font-bold`}>{formatMoney(sym, p.total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Totals({ doc, sym }) {
  const Row = ({ l, v, strong, neg }) => (
    <div className={`flex justify-between py-1 text-[12px] ${strong ? "font-bold text-slate-900" : neg ? "text-rose-700" : "text-slate-700"}`}>
      <span>{l}</span><span className="font-mono">{v}</span>
    </div>
  );
  return (
    <div className="flex justify-end pt-4">
      <div className="w-[300px]">
        <Row l="Subtotal 小计" v={formatMoney(sym, doc.subtotal)} strong />
        {doc.discount?.amount > 0 && <Row l={`Discount 折扣${doc.discount.mode === "percent" ? ` (${doc.discount.value}%)` : ""}`} v={`-${formatMoney(sym, doc.discount.amount)}`} neg />}
        {doc.breakdown?.parts > 0 && <Row l="Spare parts · net 零件" v={formatMoney(sym, doc.breakdown.parts)} strong />}
        {Number(doc.tax) > 0 && <Row l={`Tax / GST 税费${doc.taxRate !== undefined ? ` (${doc.taxRate}%)` : ""}`} v={formatMoney(sym, doc.tax)} />}
        <div className="flex justify-between items-center mt-2 px-3 py-2 rounded" style={{ background: NAVY }}>
          <span className="text-white font-bold text-[13px]">TOTAL 合计</span>
          <span className="text-white font-mono font-black text-[18px]">{formatMoney(sym, doc.total)}</span>
        </div>
      </div>
    </div>
  );
}

// "Label: value" lines from settings.bank_details become labelled rows; plain lines print as notes
const PaymentLine = ({ line }) => {
  const i = line.indexOf(":");
  if (i < 1) return <div className="text-slate-500">{line}</div>;
  return <div><span className="text-slate-500">{line.slice(0, i)}</span> <span className="font-semibold text-slate-900 font-mono">{line.slice(i + 1).trim()}</span></div>;
};

// Official PayNow QR issued by the bank for UEN 40069900A — bundled with the app build, not editable from Settings.
export const OFFICIAL_PAYNOW_QR = `${process.env.PUBLIC_URL || ""}/brand/paynow-qr.jpg`;
// Rendered as a CSS background-image (data URL): the most reliable raster path through html2canvas for PDF / WhatsApp+PDF.
let qrDataUrl = null;
const qrDataUrlPromise = fetch(OFFICIAL_PAYNOW_QR).then((r) => r.blob()).then((b) => new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(b); })).then((u) => (qrDataUrl = u)).catch(() => null);
function QrImage({ size = 112 }) {
  const [src, setSrc] = React.useState(qrDataUrl || OFFICIAL_PAYNOW_QR);
  React.useEffect(() => { qrDataUrlPromise.then((u) => u && setSrc(u)); }, []);
  return <img src={src} alt="PayNow QR — Hup Ley Huat Motor Spray Painting Svcs" className="w-[112px] h-[112px] object-contain" crossOrigin="anonymous" data-testid="paynow-qr-img" data-ready={src.startsWith("data:") ? "1" : undefined} />;
}

export function PaymentBlock({ shop, amount, reference, footer, showQr = true }) {
  if (!shop.bankDetails && !shop.paynowUen && !footer) return null;
  const lines = String(shop.bankDetails || "").split("\n").map((l) => l.trim()).filter(Boolean);
  return (
    <div className="mt-5 rounded border border-slate-200 p-3 text-[11px] text-slate-700 flex gap-4 items-start" data-testid="invoice-payment-block">
      <div className="flex-1 space-y-0.5">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Payment Method · 付款方式</div>
        {lines.map((l, idx) => <PaymentLine key={`${idx}-${l}`} line={l} />)}
        {footer && <div className="mt-2 text-slate-500">{footer}</div>}
      </div>
      {showQr && (
        <div className="text-center shrink-0" data-testid="paynow-qr">
          <div className="rounded border border-slate-200 p-1 bg-white w-[122px] mx-auto">
            <QrImage />
          </div>
          <div className="text-[10px] font-bold mt-1" style={{ color: "#7C1A78" }}>PayNow · Scan to pay</div>
          <div className="text-[9px] text-slate-600 font-mono">Enter {formatMoney(shop.sym, amount)} · Ref {reference}</div>
          {shop.paynowUen && <div className="text-[9px] text-slate-500 font-mono">UEN {shop.paynowUen}</div>}
        </div>
      )}
    </div>
  );
}

// A4 statement of account: every unpaid invoice for one (B2B) customer
export const StatementDocument = forwardRef(function StatementDocument({ doc }, ref) {
  if (!doc) return null;
  const sym = doc.shop.sym;
  const th = "text-left text-[10px] uppercase tracking-wider text-slate-500 py-2 border-b border-slate-300";
  const td = "py-2 text-[12px] border-b border-slate-100 align-top";
  return (
    <div ref={ref} className="bg-white text-slate-900 font-sans" style={{ width: A4_WIDTH_PX, minHeight: A4_HEIGHT_PX, padding: "36px 40px", boxSizing: "border-box" }} data-testid="statement-document">
      <Header doc={doc} />
      <div className="flex justify-between gap-6 mt-5">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Statement for · 客户</div>
          <div className="text-[15px] font-bold">{doc.customer.name}</div>
          {doc.customer.uen && <div className="text-[11px] text-slate-600">UEN {doc.customer.uen}</div>}
          {doc.customer.address && <div className="text-[11px] text-slate-600">{doc.customer.address}</div>}
          {doc.customer.contact && <div className="text-[11px] text-slate-600">Attn: {doc.customer.contact}</div>}
          <div className="text-[12px] text-slate-700 font-mono">{[doc.customer.phone, doc.customer.email].filter(Boolean).join(" · ")}</div>
        </div>
        <div className="text-right">
          <div className="text-[28px] font-black tracking-tight" style={{ color: NAVY }}>STATEMENT</div>
          <div className="text-[13px] text-slate-500 -mt-1">对账单 · of account</div>
          <div className="mt-3 text-[12px] text-slate-700"><span className="text-slate-500">Date</span> <span className="font-semibold">{fmtDate(doc.statementDate)}</span></div>
          <div className="text-[12px] text-slate-700"><span className="text-slate-500">Terms</span> {doc.customer.termsDays}-day</div>
        </div>
      </div>
      <table className="w-full mt-6 border-collapse">
        <thead><tr><th className={th}>Invoice</th><th className={th}>Quote</th><th className={th}>Vehicle</th><th className={th}>Issued</th><th className={th}>Due</th><th className={`${th} text-right`}>Status</th><th className={`${th} text-right`}>Amount</th></tr></thead>
        <tbody>
          {doc.invoices.map((inv) => (
            <tr key={inv.id}>
              <td className={`${td} font-mono font-semibold`}>{inv.invoice_no}</td>
              <td className={`${td} font-mono text-slate-500`}>{inv.quote_no}</td>
              <td className={td}>{[inv.plate, inv.vehicle].filter(Boolean).join(" · ")}</td>
              <td className={td}>{fmtDate(inv.issue_date)}</td>
              <td className={td}>{fmtDate(inv.due_date)}</td>
              <td className={`${td} text-right font-semibold`} style={{ color: inv.overdue ? "#b91c1c" : "#b45309" }}>{inv.overdue ? `Overdue ${inv.days_overdue}d` : "Unpaid"}</td>
              <td className={`${td} text-right font-mono font-bold`}>{formatMoney(sym, inv.total)}</td>
            </tr>
          ))}
          {doc.invoices.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-500 text-[12px]">No outstanding invoices — thank you!</td></tr>}
        </tbody>
      </table>
      <div className="flex justify-end mt-4">
        <div className="w-[300px] text-[12px]">
          {doc.overdueDue > 0 && <div className="flex justify-between py-1 text-red-700"><span>Overdue 逾期</span><span className="font-mono font-semibold">{formatMoney(sym, doc.overdueDue)}</span></div>}
          <div className="flex justify-between py-2 border-t-2 text-[15px] font-black" style={{ borderColor: NAVY }}><span>TOTAL DUE 应付总额</span><span className="font-mono" style={{ color: NAVY }}>{formatMoney(sym, doc.totalDue)}</span></div>
        </div>
      </div>
      {doc.totalDue > 0 && <PaymentBlock shop={doc.shop} amount={doc.totalDue} reference={`STMT ${doc.customer.name.slice(0, 18)}`} footer={doc.shop.invoiceFooter} />}
      <div className="mt-10 pt-4 border-t border-slate-200 text-[11px] text-slate-500 flex justify-between gap-6">
        <span className="max-w-[420px]">Please quote the invoice number(s) when making payment. 付款时请注明发票号码。</span>
        <span className="whitespace-nowrap">Thank you · 谢谢 — {doc.shop.name}</span>
      </div>
    </div>
  );
});

// A4-styled printable quotation. Rendered offscreen; used by print and PDF export.
const QuoteDocument = forwardRef(function QuoteDocument({ doc }, ref) {
  if (!doc) return null;
  const sym = doc.shop.sym;
  return (
    <div ref={ref} className="bg-white text-slate-900 font-sans" style={{ width: A4_WIDTH_PX, minHeight: A4_HEIGHT_PX, padding: "36px 40px", boxSizing: "border-box" }} data-testid="quote-document">
      <Header doc={doc} />
      <Meta doc={doc} />
      <Items doc={doc} sym={sym} />
      <Totals doc={doc} sym={sym} />
      {doc.kind === "invoice" && doc.status === "paid" ? (
        <div className="mt-5 rounded border p-3 text-[11px] flex items-center gap-3" style={{ borderColor: "#86efac", background: "#f0fdf4", color: "#166534" }} data-testid="invoice-paid-block">
          <div className="text-[22px] font-black leading-none">✓</div>
          <div><div className="font-bold text-[12px]">Payment received — thank you! 款已收到，谢谢！</div><div>Received {fmtDate(doc.paidAt || doc.created_at)} · Ref {doc.ref}. We appreciate your business and look forward to serving you again.</div></div>
        </div>
      ) : doc.kind === "invoice" ? <PaymentBlock shop={doc.shop} amount={doc.total} reference={doc.ref} footer={doc.shop.invoiceFooter} />
      : <PaymentBlock shop={doc.shop} amount={doc.total} reference={doc.ref} footer="Deposit / payment by PayNow · 可先付押金 — quote the quote number as reference. Balance on collection." />}
      {doc.notes && <p className="text-[12px] text-slate-600 italic mt-6">{doc.notes}</p>}
      <div className="mt-10 pt-4 border-t border-slate-200 text-[11px] text-slate-500 flex justify-between gap-6">
        <span className="max-w-[420px]">{doc.kind === "invoice" ? "" : doc.shop.footer}</span>
        <span className="whitespace-nowrap">Thank you · 谢谢 — {doc.shop.name}</span>
      </div>
    </div>
  );
});

export default QuoteDocument;
